import React from 'react';

import {
  Box,
} from '@mui/material';

import {
  BooleanInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
} from 'react-admin';

import { useWatch } from 'react-hook-form';

import { capitalize } from 'lodash';

import SmartDateInput from '../../../../customFields/SmartDateInput';
import SelectArrayInput from '../../../layout/inputFields/SelectArrayInput';
import caseStatuses from '../../../../constants/caseStatuses';
import ReferenceEntitiesInput from '../../../../customFields/ReferenceEntitiesInput';
import isPastDate from '../../../utils/reports/isPastDate';
import dateRangeValidation from '../../../utils/reports/dateRangeValidation';
import RuleSelector from '../../../utils/RuleSelector';
import FieldBox from '../FieldBox';
import RangeFields from '../RangeFields';

export const caseOverviewTransform = (data: any) => ({
  ...data,
  report: {
    ...data.report,
    associatedCases: data.report.entityIds?.length > 0 ? data.report.associatedCases : undefined,
  },
});

const CaseOverview = () => {
  const entityIds = useWatch({ name: 'report.entityIds' });
  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <FieldBox title="Date range" description="Only cases created within this date range are included. 'Date from' defaults to 1970-01-01; 'date to' defaults to the generation date.">
        <RangeFields
          from={(
            <SmartDateInput fullWidth validate={[isPastDate, dateRangeValidation]} source="report.from" label="From" helperText={false} />
          )}
          to={(
            <SmartDateInput fullWidth validate={[dateRangeValidation]} source="report.to" label="To" helperText={false} />
          )}
        />
      </FieldBox>
      <FieldBox title="Rules" description="Filter to only include cases triggered by these rules. Leave empty to include cases from all rules.">
        <RuleSelector source="report.triggerRuleIds" label="" field="id" helperText={false} />
      </FieldBox>
      <FieldBox title="Tags" description="Filter to only include cases that have these tags assigned. Leave empty to include cases with any tag.">
        <ReferenceArrayInput perPage={100} source="report.tagIds" reference="tags" filter={{ active: true }}>
          <AutocompleteArrayInput
            fullWidth
            label=""
            filterToQuery={(searchText: string) => ({ name: searchText })}
            optionText="name"
            helperText={false}
          />
        </ReferenceArrayInput>
      </FieldBox>
      <FieldBox title="Statuses" description="Filter to only include cases with these statuses. Leave empty to include cases of all statuses.">
        <SelectArrayInput
          fullWidth
          label=""
          choices={Object.values(caseStatuses).map((caseStatus) => ({
            name: capitalize(caseStatus),
            id: caseStatus,
          }))}
          source="report.statuses"
          helperText={false}
        />
      </FieldBox>
      <FieldBox title="Entities" description="Filter to only include cases where these entities are the subject. When entities are selected, you can also choose to include associated cases.">
        <ReferenceEntitiesInput source="report.entityIds" label="" helperText={false} />
      </FieldBox>
      {entityIds?.length > 0 && (
        <FieldBox
          title="Include associated cases"
          description="When enabled, associated cases are included in the report results. Associated Cases are cases where the Entity in question has a role in a related transaction to another case, but is not the subject of the case (such as recipient, initiator or processor of a suspicious transaction)"
          inline
        >
          <BooleanInput source="report.associatedCases" label="" helperText={false} />
        </FieldBox>
      )}
      <FieldBox title="Processors" description="Filter to only include cases where these entities are processors of the suspicious transaction.">
        <ReferenceEntitiesInput source="report.processorIds" label="" helperText={false} />
      </FieldBox>
      <FieldBox title="Sub-processors" description="Filter to only include cases where these entities are sub-processors of the suspicious transaction.">
        <ReferenceEntitiesInput source="report.subProcessorIds" label="" helperText={false} />
      </FieldBox>
      <FieldBox title="Recipients" description="Filter to only include cases where these entities are recipients of the suspicious transaction.">
        <ReferenceEntitiesInput source="report.recipientIds" label="" helperText={false} />
      </FieldBox>
      <FieldBox title="Initiators" description="Filter to only include cases where these entities are initiators of the suspicious transaction.">
        <ReferenceEntitiesInput source="report.initiatorIds" label="" helperText={false} />
      </FieldBox>
    </Box>
  );
};

export default CaseOverview;
